import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { Campaign } from '../../../../types/campaign';
import { updateOrderStatusByCampaign } from '../../../../api/campaignApi';
import Select, { SingleValue } from 'react-select';
import { createOption, Option } from '../../../../helpers/selectHelpers';
import { OrderStatus } from '../../../../types/order';
import { Loader2 } from 'lucide-react';

interface BulkActionStatusCampaignModalProps {
    campaign: Campaign;
    isOpen: boolean;
    toggle: () => void;
    onUpdateData: (newData: Campaign) => void;
}

const fromStatusOptions = [
    createOption('Rascunho', OrderStatus.DRAFT),
];

const toStatusOptions = [
    createOption('Pronto', OrderStatus.READY),
];

const BulkActionStatusCampaignModal = ({ campaign, isOpen, toggle, onUpdateData }: BulkActionStatusCampaignModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [selectFromStatusValue, setSelectFromStatusValue] = useState<SingleValue<Option>>();
    const [selectToStatusValue, setSelectToStatusValue] = useState<SingleValue<Option>>();

    const toggleModal = () => {
        toggle();
    };

    const selectFromStatusOnChange = (option: SingleValue<Option>) => {
        setSelectFromStatusValue(option);
        handleForm.setFieldValue('fromStatus', option ? option.value : '');
    }

    const selectToStatusOnChange = (option: SingleValue<Option>) => {
        setSelectToStatusValue(option);
        handleForm.setFieldValue('toStatus', option ? option.value : '');
    }

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            fromStatus: '',
            toStatus: '',
        },
        validationSchema: Yup.object({
        }),
        onSubmit: (values: any) => {
            setLoading(true);
            updateOrderStatusByCampaign(campaign.id, values)
                .then((res: any) => {
                    toggleModal();
                    onUpdateData(res);
                    toast.success('Status dos pedidos atualizado com sucesso!');
                })
                .catch(() => {
                })
                .finally(() => {
                    setLoading(false);
                });
        },
    });

    return (
        <Dialog open={isOpen} onOpenChange={toggleModal}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Atualização de status em massa</DialogTitle>
                    <DialogDescription>
                        Atualize o status dos pedidos em massa.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleForm.handleSubmit();
                    return false;
                }}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="fromStatus">De Status</Label>
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                isClearable={true}
                                isSearchable={true}
                                id="fromStatus"
                                name="fromStatus"
                                options={fromStatusOptions}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        borderColor: !!(handleForm.touched.fromStatus && handleForm.errors.fromStatus) ? 'red' : '#ced4da',
                                    }),
                                }}
                                onChange={selectFromStatusOnChange}
                                value={selectFromStatusValue}
                            />
                            {handleForm.touched.fromStatus && handleForm.errors.fromStatus && (
                                <p className="text-sm text-red-500">{handleForm.errors.fromStatus}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="toStatus">Para Status</Label>
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                isClearable={true}
                                isSearchable={true}
                                id="toStatus"
                                name="toStatus"
                                options={toStatusOptions}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        borderColor: !!(handleForm.touched.toStatus && handleForm.errors.toStatus) ? 'red' : '#ced4da',
                                    }),
                                }}
                                onChange={selectToStatusOnChange}
                                value={selectToStatusValue}
                            />
                            {handleForm.touched.toStatus && handleForm.errors.toStatus && (
                                <p className="text-sm text-red-500">{handleForm.errors.toStatus}</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Atualizar
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default BulkActionStatusCampaignModal;